/*
 * The fitting section's In-Channel / Out-Channel switch.
 *
 * The section carries both types until it knows which one the reader owns. On a
 * product page it knows at render time — the channel is a metafield — so this
 * file is not even loaded there; the section prints one type and no control.
 *
 * On a landing page the answer arrives later, when the fitment engine resolves a
 * registration, so the section ships as a two-tab control and this listens for
 * `airflux:resolved` to collapse it. The event is the SAME one the sticky bar
 * follows, and the channel travels on it rather than being read out of the
 * engine's markup: the engine repaints that markup on every chip click, and a
 * "Type" chip is exactly how a car with both kinds gets switched between them.
 *
 * Unresolving is a real state, not an edge case. "Not your car?" fires the same
 * event with nothing on it, and the tabs come back — so the control is hidden,
 * never removed.
 *
 * The tab semantics only hold while there is a question. Once the type is known
 * the tablist is hidden, and a tabpanel whose tablist is gone is a role that
 * describes nothing, so the panels give theirs up and become ordinary content.
 */
(() => {
  if (customElements.get('airflux-fitting')) return;

  const CHANNELS = ['in-channel', 'out-channel'];

  class AirfluxFitting extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-fitting-tab]')];
      this.panels = [...this.querySelectorAll('[data-fitting-panel]')];
      this.tablist = this.querySelector('[data-fitting-tabs]');
      if (this.panels.length < 2 || !this.tabs.length) return;

      // What each panel is called while the question is open, so `lock` can put
      // the labels back if the reader says the car is not theirs after all.
      this.labels = new Map(this.panels.map((p) => [p, p.getAttribute('aria-labelledby')]));

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-fitting-tab]');
        if (tab) this.select(tab.dataset.fittingTab);
      });

      // Left/right walks the pair — the same roving tabindex the demo's window
      // states and the make directory's filters use, because it is the same
      // control and should not be learnt twice.
      this.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(document.activeElement);
        if (index < 0) return;
        const last = this.tabs.length - 1;
        const next = {
          ArrowRight: index + 1 > last ? 0 : index + 1,
          ArrowLeft: index - 1 < 0 ? last : index - 1,
          Home: 0,
          End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.select(this.tabs[next].dataset.fittingTab);
        this.tabs[next].focus();
      });

      this.onResolved = this.onResolved.bind(this);
      document.addEventListener('airflux:resolved', this.onResolved);

      /* Both scripts are deferred and the engine's is earlier in the document,
         so a car restored from storage can be painted — and announced — before
         this element is even defined. The add button the engine writes carries
         the same channel on it, so a missed event is caught by reading the page
         once on connect rather than by making the engine announce twice. */
      this.lock(document.querySelector('[data-airflux-add]')?.dataset.channel);
    }

    disconnectedCallback() {
      if (this.onResolved) document.removeEventListener('airflux:resolved', this.onResolved);
    }

    onResolved(event) {
      this.lock(event.detail?.channel);
    }

    /** Collapse to one type, or hand the question back. */
    lock(channel) {
      const known = CHANNELS.includes(channel);
      this.dataset.locked = String(known);
      if (this.tablist) this.tablist.hidden = known;

      for (const panel of this.panels) {
        if (known) {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
        } else {
          panel.setAttribute('role', 'tabpanel');
          const label = this.labels.get(panel);
          if (label) panel.setAttribute('aria-labelledby', label);
        }
      }

      this.select(known ? channel : this.panels[0].dataset.fittingPanel);
    }

    select(channel) {
      for (const tab of this.tabs) {
        const on = tab.dataset.fittingTab === channel;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      }
      for (const panel of this.panels) panel.hidden = panel.dataset.fittingPanel !== channel;
    }
  }

  customElements.define('airflux-fitting', AirfluxFitting);
})();
